import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { getHttpParams } from '@iv/shared/utils';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { AdvancedSearchHttpService } from 'app/advanced-search/services/advanced-search-http.service';
import { IStockFutureResponse } from 'app/core/interfaces/stock-future-list.interface';
import { Instrument } from '@iv/model/instrument';
import * as TabsActions from '../../../store/tabs/tabs.actions';

import { OptionsCalculatorComponent } from 'app/calculators/options-calculator/options-calculator.component';
import { ProbabilityCalculatorComponent } from 'app/calculators/probability-calculator/probability-calculator.component';
import { getPermissions } from '@iv/store/permissions/permissions.reducer';
import { PermissionTypes } from '@iv/store/permissions/permissions.models';
import { TabTypeEnum } from '@iv/model/tabs.model';
import { LoginService } from 'app/login.service';

@Component({
  selector: 'iv-live-calculator-wrap-component',
  template: `
    <div *ngIf="instruments.length > 0"
         class="dx-overlay-wrapper create-popup dx-popup-wrapper dx-overlay-content dx-popup-normal dx-resizable"
         style="position: relative; width: 900px; height: 600px;">
      <div class="dx-popup-content dx-template-wrapper ng-star-inserted"
           style="box-sizing: border-box;background-color: #272B29;padding:16px;height: 100%;">
        <iv-live-common-calculator-header
          [userPermissionMode]="mode"
          [isControlsShown]="+sessionStorage.getItem('currentStockRegion') === 1"
          [isEod] = "isEODOptions && isEODProbability"
          (switchMode)="switchCalculatorsMode()"
          (switchType)="switchCalculatorType($event)"
        >
        </iv-live-common-calculator-header>
        <iv-live-options-calculator
          [ngStyle]="{'display': calculatorType === 'options' ? 'inline' : 'none'}"
          [instrument]="selectedInstrument"
          [instruments]="instruments"
          [mode]="mode"
          (onInstrumentChange)="changeCalculatorsInstrument($event)"
          (returnRegionEvent)="returnRegionOptions($event.region)"
        ></iv-live-options-calculator>
        <iv-live-probability-calculator
          [ngStyle]="{'display': calculatorType === 'probability' ? 'inline' : 'none'}"
          [instrument]="selectedInstrument"
          [instruments]="instruments"
          [mode]="mode"
          [currentTab]="tab"
          (onInstrumentChange)="changeCalculatorsInstrument($event)"
          (returnRegionEvent)="returnRegionProbability($event.region)"
        ></iv-live-probability-calculator>
      </div>
    </div>`,
  styles: [
    ':host ::ng-deep .calculator-search {}'
  ]
})
// TODO: Привести в порядок названия методов и свойств
export class CalculatorWrapComponent implements OnInit {
  @ViewChild(OptionsCalculatorComponent, {static: false}) optionsCalculator !: OptionsCalculatorComponent;
  @ViewChild(ProbabilityCalculatorComponent, {static: false}) probabilityCalculator !: ProbabilityCalculatorComponent;

  tab = {
    "id": "_vim9b35wv",
    "type": "instrument",
    "active": true,
    "component": "OptionChainComponent",
    "name": "SPX",
    "description": "S&P 500 Index (SPX)",
    "data": {
      "instrument": {
        "stockId": 9327,
        "regionId": 1,
        "symbol": "SPX",
        "exchange": "CBOE",
        "company": "S&P 500 Index (SPX)",
        "region": "USA",
        "stockType": "I",
        "startDate": "1988-01-04T00:00:00",
        "endDate": "2079-06-06T00:00:00",
        "delisted": false,
        "hasOptions": true
      },
      "chartType": 2,
      "serviceTabs": [
        {
          "active": true,
          "id": 1,
          "component": "OptionMonitorComponent",
          "name": "Options Chain",
          "filters": {
            "expirations": {
              "id": "ALL",
              "value": "All",
              "type": "type"
            },
            "strikesN": "5",
            "type": {
              "name": "Call&Put",
              "value": ""
            },
            "center": 4378.41
          },
          "visibleColumns": [
            "strike",
            "bid",
            "ask",
            "volume",
            "ivint",
            "delta",
            "vega"
          ]
        }
      ],
      "summaryLineVisibleColumns": [
        "price",
        "open",
        "prevClose",
        "changeopen",
        "high",
        "low",
        "bidSize",
        "mid",
        "hv",
        "askSize",
        "stockVol",
        "optVol",
        "ivx",
        "openInterest",
        "callVol",
        "ivxchangeopen",
        "putVol",
        "52whilo"
      ]
    }
  };
  selectedInstrument = this.tab.data.instrument;
  mode: PermissionTypes;
  instruments: Instrument[] = [];
  isEODOptions: boolean = false;
  isEODProbability: boolean = false;
  calculatorType: 'options' | 'probability' = 'options';
  regionIdOptions: number;
  regionIdProbability: number;

  protected readonly sessionStorage: Storage = sessionStorage;

  constructor(
    private loginService: LoginService,
    private advancedSearchHttpService: AdvancedSearchHttpService,
    @Inject(AppStore) private store: Redux.Store<AppState>,
  ) {
    this.store.dispatch(TabsActions.addTab(this.tab));
    this.store.dispatch(TabsActions.selectTab(this.tab));
  }

  async ngOnInit(): Promise<void> {
    await this.loginService.initUser();

    const defaultParams = getHttpParams({region: '0', matchingType: 'CONTAINS', sortField: 'SYMBOL'});
    this.advancedSearchHttpService.getListEntities(defaultParams, false)
      .subscribe((data: IStockFutureResponse<Instrument[]>) => {
        this.instruments = data.page.map(instrument => Object.assign(new Instrument(), instrument));
      });

    this.mode = getPermissions(this.store.getState()).find(permission =>
      this.tab.type === TabTypeEnum.OPTION_CHAIN
        ? permission === PermissionTypes.DELAYED_STOCKS || permission === PermissionTypes.RT_STOCKS
        : permission === PermissionTypes.EOD_FUTURES || permission === PermissionTypes.RT_FUTURES
    );
  }

  switchCalculatorsMode(): void {
    this.switchModeOptions();
    this.switchModeProbability();
  }

  switchModeOptions(): void {
    this.optionsCalculator?.switchMode();
    this.isEODOptions = !this.isEODOptions;
  }

  switchModeProbability(): void {
    this.probabilityCalculator?.switchMode();
    this.isEODProbability = !this.isEODProbability;
  }

  switchCalculatorType(type: 'options' | 'probability'): void {
    this.calculatorType = type;
  }

  changeCalculatorsInstrument(instrument: any): void {
    this.optionsCalculator.instrument = instrument;
    this.probabilityCalculator.id = instrument.stockId;
  }

  returnRegionOptions(region: number): void {
    this.regionIdOptions = region;
    if (this.regionIdOptions === 2 || this.regionIdOptions === 3 || this.regionIdOptions === 4) {
      this.isEODOptions = true;
    }
  }

  returnRegionProbability(region: number): void {
    this.regionIdProbability = region;
    if (this.regionIdProbability === 2 || this.regionIdProbability === 3 || this.regionIdProbability === 4) {
      this.isEODProbability = true;
    }
  }
}
